import { z } from "zod";

/**
 * The wording of WhatsApp messages that is the retailer's to replace: button titles, list
 * headers and the location question. The renderer's built-in copy (English, Arabic, Arabizi,
 * French) is used for anything not set here.
 */
export const WHATSAPP_LABELS = [
	"checkout",
	"editBasket",
	"keepShopping",
	"add",
	"similar",
	"basket",
	"addAll",
	"chooseItems",
	"steps",
	"basketTitle",
	"listTitle",
	"pricesFrom",
	"locationAsk",
	"deliverAgain",
	"somewhereElse",
	"newLocation",
	"pickUpThere",
	"anotherLocation",
	"talkToPerson",
] as const;
export type WhatsappLabel = (typeof WHATSAPP_LABELS)[number];

/**
 * One label in the languages the retailer wrote it in, keyed by the language names the agent
 * reads off a message ("English", "Arabic", "Arabizi", "French"). The renderer cuts a label to
 * Meta's limit for where it goes (20 characters on a reply button, 24 on a list row).
 */
const labelTexts = z.record(
	z.string().min(1).max(40),
	z.string().trim().min(1).max(60),
);

/**
 * How the agent behaves on the retailer's WhatsApp numbers. Versioned with the rest of the
 * settings, so a change is benched (the WHATSAPP eval suite) before it is published.
 */
export const whatsappSchema = z
	.object({
		/** A voice note is answered with a voice note ("mirror"), or always in writing. */
		voiceReplies: z.enum(["mirror", "text"]).default("mirror"),
		/**
		 * When the shopper is asked where to deliver, for a retailer with several branches:
		 * before any product is shown ("first": prices and stock are the branch's, so nothing
		 * priced goes out and nothing goes in the basket until the branch is set, and the
		 * shopper is asked delivery or pickup when branches offer both), with the first basket
		 * summary (products show before that with the default branch's prices), only at
		 * checkout, or never (one branch, pickup only).
		 */
		askLocation: z
			.enum(["first", "when_needed", "at_checkout", "never"])
			.default("when_needed"),
		/**
		 * Products as WhatsApp product messages from the Meta catalog when every one is there
		 * ("auto"), or always as image cards ("media") or a plain list ("list").
		 */
		productMessages: z.enum(["auto", "media", "list"]).default("auto"),
		/** Read a photo the shopper sends (a product, a list, a dish) into a request. */
		readPhotos: z.boolean().default(true),
		/**
		 * Sent once per customer, with the first reply. {assistant}, {retailer} and {link} (the
		 * retailer's privacy page) are filled in.
		 */
		privacyLine: z
			.string()
			.trim()
			.min(1)
			.max(300)
			.default(
				"{assistant} is {retailer}'s AI assistant. Ask for a person any time. Privacy: {link}",
			),
		/** Replacement wording; an absent label or language is the built-in copy. */
		labels: z
			.object(
				Object.fromEntries(
					WHATSAPP_LABELS.map((label) => [
						label,
						labelTexts.optional(),
					]),
				) as Record<WhatsappLabel, z.ZodOptional<typeof labelTexts>>,
			)
			.prefault({}),
	})
	.prefault({});
export type WhatsappSettings = z.infer<typeof whatsappSchema>;
